import { useState, useEffect } from "react";
import { BlogMeta } from "../../../types/blog.model";
import http from "../../../api/http-client";
import { useLocale } from "../../../context/LocaleContext";

export interface UseBlogOptions {
  /** Include draft (unpublished) posts in the response, for the Course Roadmap
   *  view. Never set this for the shared reader layout — its sidebar TOC and
   *  prev/next nav must never link to locked content. */
  includeDraftMeta?: boolean;
}

export function useBlog(blogSlug: string | undefined, options?: UseBlogOptions) {
  const [blog, setBlog] = useState<BlogMeta | null>(null);
  const [blogPostsTitle, setBlogPostsTitle] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { locale } = useLocale();
  const includeDraftMeta = options?.includeDraftMeta ?? false;

  useEffect(() => {
    if (!blogSlug) return;
    let isMounted = true;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await http.get<BlogMeta>(`/blog/${blogSlug}`, {
          params: { locale, ...(includeDraftMeta && { includeDraftMeta: 'true' }) },
        });
        const data = response.data;

        if (!isMounted) return;

        setBlog(data);
        setBlogPostsTitle(data?.posts?.map((post) => post.title) || []);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        // Guard: only update state if still mounted, otherwise we leave
        // loading=false with blog=null which causes a not-found flash.
        if (isMounted) setLoading(false);
      }
    };

    fetchBlog();

    return () => {
      isMounted = false;
    };
  }, [blogSlug, locale, includeDraftMeta]);

  return { blog, blogPostsTitle, loading, error };
}